// shared-components/features/transitions/AnimatedComponents.jsx
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { useFadeIn, useHoverAnimation } from "../../hooks/useAnimationHooks.js";
import { cn } from "@shared/core/lib/utils";
import { forwardRef } from "react";

/**
 * AnimatedSection - Section that animates in when scrolled into view
 */
export const AnimatedSection = forwardRef(
  (
    {
      children,
      className,
      direction = "up",
      delay = 0,
      distance = 50,
      ...props
    },
    ref,
  ) => {
    const { variants, initial, animate, inViewRef } = useFadeIn({
      direction,
      delay,
      distance,
    });

    // Combine refs
    const setRefs = (element) => {
      if (ref) {
        // Handle callback refs
        if (typeof ref === "function") {
          ref(element);
        } else {
          // Handle object refs
          ref.current = element;
        }
      }
      if (inViewRef) {
        inViewRef(element);
      }
    };

    return (
      <motion.section
        ref={setRefs}
        className={cn(className)}
        initial={initial}
        animate={animate}
        variants={variants}
        {...props}
      >
        {children}
      </motion.section>
    );
  },
);

AnimatedSection.displayName = "AnimatedSection";

AnimatedSection.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  direction: PropTypes.oneOf(["up", "down", "left", "right"]),
  delay: PropTypes.number,
  distance: PropTypes.number,
};

/**
 * AnimatedButton - Button with hover/tap animations
 */
export const AnimatedButton = forwardRef(
  (
    {
      children,
      className,
      scale = 1.05,
      shadow = true,
      rotate = 0,
      color,
      ...props
    },
    ref,
  ) => {
    const hoverProps = useHoverAnimation({
      scale,
      shadow,
      rotate,
      color,
    });

    return (
      <motion.button
        ref={ref}
        className={cn(className)}
        {...hoverProps}
        {...props}
      >
        {children}
      </motion.button>
    );
  },
);

AnimatedButton.displayName = "AnimatedButton";

AnimatedButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  scale: PropTypes.number,
  shadow: PropTypes.bool,
  rotate: PropTypes.number,
  color: PropTypes.string,
};

/**
 * AnimatedCard - Card with hover animations
 */
export const AnimatedCard = forwardRef(
  (
    {
      children,
      className,
      scale = 1.03,
      shadow = true,
      rotate = 0,
      direction = "up",
      delay = 0,
      ...props
    },
    ref,
  ) => {
    const { variants, initial, animate, inViewRef } = useFadeIn({
      direction,
      delay,
    });

    const hoverProps = useHoverAnimation({
      scale,
      shadow,
      rotate,
    });

    // Combine refs
    const setRefs = (element) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(element);
        } else {
          ref.current = element;
        }
      }
      if (inViewRef) {
        inViewRef(element);
      }
    };

    return (
      <motion.div
        ref={setRefs}
        className={cn(className)}
        initial={initial}
        animate={animate}
        variants={variants}
        {...hoverProps}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

AnimatedCard.displayName = "AnimatedCard";

AnimatedCard.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  scale: PropTypes.number,
  shadow: PropTypes.bool,
  rotate: PropTypes.number,
  direction: PropTypes.oneOf(["up", "down", "left", "right"]),
  delay: PropTypes.number,
};

/**
 * AnimatedImage - Image that fades in when loaded and has hover effects
 */
export const AnimatedImage = forwardRef(
  (
    { src, alt, className, scale = 1.05, scaleOnHover = true, ...props },
    ref,
  ) => {
    const { variants, initial, animate, inViewRef } = useFadeIn({
      distance: 20,
    });

    const hoverAnimationProps = useHoverAnimation({ scale, shadow: false });
    const hoverProps = scaleOnHover ? hoverAnimationProps : {};

    // Combine refs
    const setRefs = (element) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(element);
        } else {
          ref.current = element;
        }
      }
      if (inViewRef) {
        inViewRef(element);
      }
    };

    return (
      <motion.img
        ref={setRefs}
        src={src}
        alt={alt}
        className={cn(className)}
        initial={initial}
        animate={animate}
        variants={variants}
        {...hoverProps}
        {...props}
      />
    );
  },
);

AnimatedImage.displayName = "AnimatedImage";

AnimatedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  scale: PropTypes.number,
  scaleOnHover: PropTypes.bool,
};

/**
 * AnimatedList - Container for list items with staggered animations
 */
export const AnimatedList = forwardRef(
  (
    { children, className, staggerDelay = 0.1, initialDelay = 0.2, ...props },
    ref,
  ) => {
    const { inViewRef } = useFadeIn({
      delay: 0,
    });

    // Combine refs
    const setRefs = (element) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(element);
        } else {
          ref.current = element;
        }
      }
      if (inViewRef) {
        inViewRef(element);
      }
    };

    return (
      <motion.div
        ref={setRefs}
        className={cn(className)}
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 1 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: staggerDelay,
              delayChildren: initialDelay,
            },
          },
        }}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

AnimatedList.displayName = "AnimatedList";

AnimatedList.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  staggerDelay: PropTypes.number,
  initialDelay: PropTypes.number,
};

/**
 * AnimatedListItem - Item for use within AnimatedList
 */
export const AnimatedListItem = forwardRef(
  ({ children, className, direction = "up", distance = 20, ...props }, ref) => {
    return (
      <motion.div
        ref={ref}
        className={cn(className)}
        variants={{
          hidden: {
            opacity: 0,
            y:
              direction === "up"
                ? distance
                : direction === "down"
                  ? -distance
                  : 0,
            x:
              direction === "left"
                ? distance
                : direction === "right"
                  ? -distance
                  : 0,
          },
          visible: {
            opacity: 1,
            y: 0,
            x: 0,
          },
        }}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

AnimatedListItem.displayName = "AnimatedListItem";

AnimatedListItem.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  direction: PropTypes.oneOf(["up", "down", "left", "right"]),
  distance: PropTypes.number,
};

/**
 * AnimatedText - Text that animates character by character
 */
export const AnimatedText = forwardRef(
  (
    {
      text,
      className,
      type = "paragraph",
      staggerDelay = 0.01,
      initialDelay = 0.1,
      ...props
    },
    ref,
  ) => {
    const { inViewRef } = useFadeIn({
      delay: 0,
    });

    // Combine refs
    const setRefs = (element) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(element);
        } else {
          ref.current = element;
        }
      }
      if (inViewRef) {
        inViewRef(element);
      }
    };

    const letters = Array.from(text);

    const TextComponent = type === "heading" ? motion.h2 : motion.p;

    return (
      <TextComponent
        ref={setRefs}
        className={cn(className)}
        initial="hidden"
        animate="visible"
        {...props}
      >
        <span className="sr-only">{text}</span>
        <motion.span
          aria-hidden="true"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: staggerDelay,
                delayChildren: initialDelay,
              },
            },
          }}
        >
          {letters.map((letter, index) => (
            <motion.span
              key={index}
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0 },
              }}
            >
              {letter === " " ? "\u00A0" : letter}
            </motion.span>
          ))}
        </motion.span>
      </TextComponent>
    );
  },
);

AnimatedText.displayName = "AnimatedText";

AnimatedText.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  type: PropTypes.oneOf(["paragraph", "heading"]),
  staggerDelay: PropTypes.number,
  initialDelay: PropTypes.number,
};
